import React, { FC, Fragment, useContext, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Divider, Tooltip, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { NavLink } from 'react-router-dom';
import { logout } from 'utils/logout';
import { Organization } from 'models/organization';
import { useMutation } from '@apollo/client';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
import { switchOrganizationTemplate } from 'apollo/templates/user/index';
import ImpersonateUserOrganizationsDialog from 'components/shared/impersonate-user-organizations-dialog/impersonate-user-organizations-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import AboutDenthubDialog from 'components/shared/about-denthub-dialog/about-denthub-dialog.component';
import LabRushDialog from 'components/shared/lab-rush-dialog/lab-rush-dialog.component';

const { TOKEN_KEY, COOKIE_OPTIONS, LAB_ID_KEY } = CONSTANTS;

const AccountMenu: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'common']);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [orgDialogOpen, setOrgDialogOpen] = useState(false);
    const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
    const [labRushOpen, setLabRushOpen] = useState(false);
    const currentUser = currentUserVar();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [switchOrganization] = useMutation(switchOrganizationTemplate, {
        fetchPolicy: 'network-only',
        errorPolicy: 'none',
        onCompleted: ({ switchOrganization }) => {
            if (!switchOrganization?.token) return;
            Cookies.set(TOKEN_KEY, `JWT ${switchOrganization.token}`, COOKIE_OPTIONS as Cookies.CookieAttributes);
            window.location.href = '/orders/list';
        },
        onError: (error) => {
            if (error.message === 'White label user do not have access to login') {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('common:WHITE_LABEL_DENTIST_ACCESS_DENIED'),
                });
            }
        },
    });

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOrgDialogOpen = () => {
        handleClose();
        setOrgDialogOpen(true);
    };

    const handleOrgDialogClose = () => {
        setOrgDialogOpen(false);
    };

    const handleOrgConfirm = async (organizationId: string, wlLabId: string | undefined) => {
        if (wlLabId) {
            const result = await switchOrganization({ variables: { organizationId, wlLabId } });
            if (result?.data?.switchOrganization?.token) {
                Cookies.set(LAB_ID_KEY, wlLabId, COOKIE_OPTIONS as Cookies.CookieAttributes);
            }
        } else {
            await switchOrganization({ variables: { organizationId } });
        }
    };

    // DD-3626: a lab a super admin deactivated cannot be switched to (the backend refuses it too)
    const switchableLinkedOrganizations = currentUser?.linkedOrganizations?.filter((linkedOrganization) => {
        const organization = linkedOrganization.organization as Organization | null;
        return !(organization?.type === 'lab' && organization.active === false);
    });
    let isAllowChangeOrg = switchableLinkedOrganizations && switchableLinkedOrganizations.length > 1;
    if (switchableLinkedOrganizations && switchableLinkedOrganizations.length === 1) {
        const singleOrg = switchableLinkedOrganizations[0].organization as Organization | null;
        isAllowChangeOrg = (singleOrg?.isWhiteLabeled ?? false) && (singleOrg?.connectedLabs?.length ?? 0) > 1;
    }

    return (
        <Fragment>
            <IconButton
                edge="end"
                aria-controls="account-menu"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                size="large"
                sx={{ color: '#fff' }}
                className="wl-text"
                data-test="main-app-bar.account-menu-btn"
            >
                <PersonIcon />
            </IconButton>

            <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box px={2} py={0.5} display="flex" alignItems="center" gap={1.5} width={260}>
                    <Avatar src={currentUser.signedUrl} sx={{ width: 44, height: 44, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {currentUser.name?.firstName} {currentUser.name?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {translate(`common:${currentUser.role}`)}
                        </Typography>
                        {(currentUser.organization as Organization)?.name && (
                            <Tooltip title={(currentUser.organization as Organization).name} placement="bottom-start">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    display="block"
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {(currentUser.organization as Organization).name}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                <Box my={1}>
                    <Divider />
                </Box>

                {isAllowChangeOrg && (
                    <MenuItem onClick={handleOrgDialogOpen} data-test="main-app-bar.change-org-btn">
                        <ListItemIcon>
                            <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText primary={translate('CHANGE_ORG')} />
                    </MenuItem>
                )}

                <MenuItem
                    component={NavLink}
                    onClick={handleClose}
                    to="/user-settings/account-settings"
                    data-test="main-app-bar.account-settings-btn"
                >
                    <ListItemIcon>
                        <SettingsOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={translate('USER_SETTINGS')} />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setAboutDialogOpen(true);
                    }}
                    data-test="main-app-bar.about-denthub-btn"
                >
                    <ListItemIcon>
                        <InfoOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={translate('ABOUT_DENTHUB')} />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setLabRushOpen(true);
                    }}
                    data-test="main-app-bar.lab-rush-btn"
                >
                    <ListItemIcon>
                        <SportsEsportsOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={translate('PLAY_LAB_RUSH')} />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        logout(false);
                    }}
                    data-test="main-app-bar.logout-btn"
                >
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary={translate('LOGOUT')} />
                </MenuItem>
            </Menu>

            <ImpersonateUserOrganizationsDialog
                open={orgDialogOpen}
                handleClose={handleOrgDialogClose}
                linkedOrganizations={switchableLinkedOrganizations ?? []}
                handleConfirm={handleOrgConfirm}
            />

            <AboutDenthubDialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} />
            <LabRushDialog open={labRushOpen} onClose={() => setLabRushOpen(false)} />
        </Fragment>
    );
};

export default AccountMenu;
